import PropTypes from "prop-types";
import styled from "styled-components";
import { FaPrint, FaShare, FaExclamationTriangle } from "react-icons/fa";
import ContentLayout from "../../../layout/ContentLayout";
import { H2, H3, Paragraph } from "../../../styles/AppStyles";
import { LINKS, href } from "../../../config/links";

const CalloutBox = styled.div`
  margin: 1.5rem 0;
  padding: 1.25rem;
  border-left: 5px solid ${({ $type, theme }) => {
    if ($type === "do") return "#10b981";
    if ($type === "dont") return "#ef4444";
    if ($type === "warning") return "#f59e0b";
    if ($type === "tip") return "#6366f1";
    return theme.iconColor;
  }};
  background-color: ${({ $type, theme }) => {
    if ($type === "do") return "rgba(16, 185, 129, 0.08)";
    if ($type === "dont") return "rgba(239, 68, 68, 0.08)";
    if ($type === "warning") return "rgba(245, 158, 11, 0.08)";
    if ($type === "tip") return "rgba(99, 102, 241, 0.08)";
    return "rgba(99, 102, 241, 0.08)";
  }};
  border-radius: 6px;
  color: ${({ theme }) => theme.text};

  strong {
    display: block;
    margin-bottom: 0.5rem;
    font-size: 1.1rem;
    color: ${({ $type }) => {
      if ($type === "do") return "#10b981";
      if ($type === "dont") return "#ef4444";
      if ($type === "warning") return "#f59e0b";
      if ($type === "tip") return "#6366f1";
      return "#6366f1";
    }};
  }

  ul {
    margin: 0.75rem 0 0 0;
    padding-left: 1.5rem;

    li {
      margin-bottom: 0.5rem;
      line-height: 1.6;
    }
  }
`;

const ChecklistSection = styled.div`
  margin: 1.5rem 0;
  padding: 1.25rem;
  background-color: ${({ theme }) => theme.background};
  border: 1px solid ${({ theme }) => theme.iconColor};
  border-radius: 6px;
  color: ${({ theme }) => theme.text};

  h3 {
    margin-top: 0;
  }
`;

const ChecklistItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;

  input[type="checkbox"] {
    margin-top: 0.35rem;
    flex-shrink: 0;
    width: 1.1rem;
    height: 1.1rem;
    cursor: pointer;
  }

  label {
    flex: 1;
    cursor: pointer;
    line-height: 1.6;
  }
`;

const ScenarioBox = styled.div`
  margin: 1.5rem 0;
  padding: 1.25rem;
  background-color: ${({ theme }) => theme.background};
  border: 2px dashed ${({ theme }) => theme.iconColor};
  border-radius: 6px;
  color: ${({ theme }) => theme.text};

  h4 {
    margin-top: 0;
    color: ${({ theme }) => theme.iconColor};
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .scenario-text {
    font-style: italic;
    margin: 0.75rem 0;
    padding: 0.75rem;
    background-color: ${({ theme }) =>
      theme.isdarkmode ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.05)"};
    border-radius: 4px;
  }

  .response {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid ${({ theme }) => theme.iconColor};

    strong {
      color: #10b981;
      display: block;
      margin-bottom: 0.5rem;
    }

    ul {
      margin: 0;
      padding-left: 1.5rem;

      li {
        margin-bottom: 0.5rem;
      }
    }
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 1rem;
  margin: 2rem 0;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    flex-direction: column;

    button,
    a {
      width: 100%;
    }
  }
`;

const Button = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  background-color: ${({ theme }) => theme.iconColor};
  color: ${({ theme }) => theme.background};
  text-decoration: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  cursor: pointer;
  border: none;

  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const PhysicalSecurityGuide = ({ istouch, isdarkmode }) => {
  const canonicalUrl = href(LINKS.QUICK_GUIDES.PHYSICAL_SECURITY);

  // const heroImageSrc = "/images/guides/physical-security.webp";

  return (
    <ContentLayout
        istouch={istouch}
        isdarkmode={isdarkmode}
        displayTitle="Control Who Enters Your Facility"
        seoTitle="Physical Security Guide: Badge Access & Visitor Control | CSFSafe"
        canonicalUrl={canonicalUrl}
        description="Practical physical security guide for SMBs: badge systems, escort policies, clean desk practices, visitor management, and tailgating prevention."
      ogTitle="Physical Security Guide: Badge Access & Visitor Control | CSFSafe"
      ogDescription="Badge procedures, tailgating prevention, sensitive area access, and visitor management for small business facilities."
      // ogImage={heroImageSrc}
      // heroImageSrc={heroImageSrc}
      heroImageAlt="Physical security and facility access control"
      publishedLabel="Quick Reference Guide — July 22, 2026"
      showCTA={true}
    >
      <Paragraph>
        Cybersecurity isn't only digital—someone who walks into your building
        can plug in a malicious device, photograph sensitive documents, or
        access an unlocked computer directly. This guide covers the physical
        security fundamentals every small business should have in place:
        badge systems, escort policies, clean desk habits, and visitor
        management.
      </Paragraph>

      <H2>Badge Systems</H2>

      <H3>Individual Accountability</H3>

      <CalloutBox $type="do">
        <strong>Badge Procedures</strong>
        <ul>
          <li>
            Issue individual badges to every employee—never share badges
            between people
          </li>
          <li>
            Deactivate badges immediately when someone leaves the company or
            changes roles that no longer require certain access
          </li>
          <li>
            Review badge access logs periodically for unusual entry times or
            repeated failed attempts
          </li>
          <li>
            Restrict badge access to only the areas a role actually needs
            (least privilege applies physically too)
          </li>
          <li>
            Require badges to be worn visibly so unauthorized individuals
            stand out
          </li>
          <li>
            Report lost or stolen badges immediately so they can be
            deactivated
          </li>
        </ul>
      </CalloutBox>

      <H2>Tailgating Prevention</H2>

      <Paragraph>
        Tailgating (or "piggybacking") is when an unauthorized person follows
        an authorized employee through a secured door without badging in
        themselves—often by simply acting confident or carrying something that
        makes holding the door seem natural.
      </Paragraph>

      <H3>Stop It Before It Starts</H3>

      <CalloutBox $type="warning">
        <strong>How to Prevent Tailgating</strong>
        <ul>
          <li>
            Train employees that it's okay—expected—to politely ask an
            unfamiliar person to badge in themselves
          </li>
          <li>
            Don't hold secured doors open for people you don't recognize, even
            if they seem to be in a hurry or carrying boxes
          </li>
          <li>
            Install turnstiles or mantrap doors at high-security entry points
            where feasible
          </li>
          <li>
            Post signage reminding staff that each person must badge in
            individually
          </li>
          <li>
            Report tailgating incidents, even if they turn out to be harmless
            — this builds awareness of the pattern
          </li>
        </ul>
      </CalloutBox>

      <H2>Escort Policies</H2>

      <CalloutBox $type="tip">
        <strong>Visitors and Contractors Must Be Escorted</strong>
        <ul>
          <li>
            All visitors, delivery personnel, and contractors sign in and
            receive a visible visitor badge
          </li>
          <li>
            Visitors are escorted at all times in non-public areas—never left
            unattended near workstations, server rooms, or file storage
          </li>
          <li>
            Confirm the visitor's identity and purpose before granting entry,
            especially for anyone claiming to be "IT support" or "from the
            building"
          </li>
          <li>
            Log entry and exit times for all visitors and contractors
          </li>
          <li>
            Notify security or the front desk in advance of expected visitors
            so unexpected ones are more easily identified
          </li>
        </ul>
      </CalloutBox>

      <H2>Clean Desk Practices</H2>

      <ChecklistSection>
        <h3>End-of-Day Clean Desk Checklist</h3>
        <ChecklistItem>
          <input type="checkbox" id="item1" />
          <label htmlFor="item1">
            Lock your computer screen every time you step away, even briefly
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item2" />
          <label htmlFor="item2">
            Store sensitive documents in locked drawers or cabinets overnight
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item3" />
          <label htmlFor="item3">
            Shred documents containing sensitive data rather than tossing them
            in regular trash
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item4" />
          <label htmlFor="item4">
            Remove printed materials from shared printers promptly
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item5" />
          <label htmlFor="item5">
            Don't leave passwords, badges, or keys visible on your desk
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item6" />
          <label htmlFor="item6">
            Position monitors away from windows and public sightlines when
            handling sensitive data
          </label>
        </ChecklistItem>
      </ChecklistSection>

      <H2>Sensitive Area Access</H2>

      <CalloutBox $type="warning">
        <strong>Extra Controls for High-Risk Areas</strong>
        <ul>
          <li>
            Server rooms, network closets, and file storage areas require
            separate, restricted badge access beyond general building entry
          </li>
          <li>
            Maintain a log of everyone who enters sensitive areas, including
            employees
          </li>
          <li>
            Never prop open doors to secured areas, even temporarily
          </li>
          <li>
            Limit the number of people with access to critical infrastructure
            to those who truly need it
          </li>
          <li>
            Consider video monitoring for entry points to server rooms and
            data storage areas
          </li>
        </ul>
      </CalloutBox>

      <H2>Visitor Management</H2>

      <ChecklistSection>
        <h3>Visitor Access Checklist</h3>
        <ChecklistItem>
          <input type="checkbox" id="item7" />
          <label htmlFor="item7">
            Visitor signs in with name, company, purpose, and host contact
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item8" />
          <label htmlFor="item8">
            Visitor identity verified (ID check for unfamiliar visitors)
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item9" />
          <label htmlFor="item9">
            Visitor badge issued and worn visibly at all times
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item10" />
          <label htmlFor="item10">
            Visitor escorted in non-public areas by their host
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item11" />
          <label htmlFor="item11">
            Visitor badge collected and sign-out logged upon departure
          </label>
        </ChecklistItem>
      </ChecklistSection>

      <H2>Incident Reporting</H2>

      <CalloutBox $type="tip">
        <strong>What to Report and How</strong>
        <ul>
          <li>Unrecognized individuals in restricted or secure areas</li>
          <li>Tailgating attempts, successful or not</li>
          <li>Lost or stolen badges, keys, or access devices</li>
          <li>Propped-open secure doors or disabled access controls</li>
          <li>Suspicious USB devices or unfamiliar hardware found on-site</li>
          <li>
            Report to your security team or facilities manager immediately—
            don't wait until the next scheduled meeting
          </li>
        </ul>
      </CalloutBox>

      <H2>Real-World Scenario</H2>

      <ScenarioBox>
        <h4>
          <FaExclamationTriangle /> The "Delivery Driver" Who Wasn't
        </h4>
        <div className="scenario-text">
          A person wearing a delivery uniform and carrying a box approaches a
          secured door as an employee badges in. They ask the employee to hold
          the door since their hands are full. The employee holds the door
          without verifying the person's identity or asking them to sign in at
          the front desk.
        </div>
        <div className="response">
          <strong>What Should Have Happened:</strong>
          <ul>
            <li>
              Politely direct the person to the front desk or main entrance to
              sign in, regardless of how legitimate they appear
            </li>
            <li>
              Offer to carry the package to the front desk rather than letting
              them follow through the secure door
            </li>
            <li>
              Notify security or facilities if the person becomes insistent or
              leaves before signing in
            </li>
            <li>
              Remember: legitimate delivery personnel expect to sign in—it's
              a normal part of their job, not an inconvenience
            </li>
          </ul>
        </div>
      </ScenarioBox>

      <H2>Physical Security Best Practices for SMBs</H2>

      <Paragraph>
        Small businesses often assume physical security is only for large
        corporations with dedicated security staff. In reality, SMBs are
        frequent targets precisely because physical controls are often weaker.
        Build these habits into your culture:
      </Paragraph>
      <ul>
        <li>
          <strong>Make verification normal, not awkward:</strong> Train staff
          that asking "can I see your badge?" is expected, not rude
        </li>
        <li>
          <strong>Combine physical and digital security training:</strong>{" "}
          Include physical security scenarios in your regular security
          awareness sessions
        </li>
        <li>
          <strong>Review access logs regularly:</strong> Even a small office
          benefits from periodic review of who accessed what and when
        </li>
        <li>
          <strong>Plan for offboarding:</strong> Physical access should be
          revoked the same day as digital access when someone leaves
        </li>
      </ul>

      <H2>Related Resources</H2>
      <ul>
        <li>
          <a href={href(LINKS.QUICK_GUIDES.SOCIAL_ENGINEERING)}>
            Social Engineering Red Flags
          </a>
        </li>
        <li>
          <a href={href(LINKS.THREAT_TYPE.INSIDER_THREATS)}>
            Insider Threats: Full Article
          </a>
        </li>
        <li>
          <a href={href(LINKS.BEST_PRACTICES.ACCESS_CONTROL)}>
            Access Control &amp; Password Basics
          </a>
        </li>
        <li>
          <a href={href(LINKS.CYBERSECURITY_FRAMEWORK.POLICIES_AND_PROCEDURES)}>
            Policies &amp; Procedures
          </a>
        </li>
      </ul>

      <ButtonRow data-print="hide">
        <Button
          as="button"
          type="button"
          onClick={() => window.print()}
          style={{ fontFamily: "inherit" }}
        >
          <FaPrint /> Print or save as PDF
        </Button>
        <Button
          href="https://www.facebook.com/sharer/sharer.php?u=https://csfsafe.com/resources/quick-guides/physical-security"
          target="_blank"
          rel="noopener noreferrer"
        >
          <FaShare /> Share Guide
        </Button>
      </ButtonRow>
      </ContentLayout>
  );
};

PhysicalSecurityGuide.propTypes = {
  istouch: PropTypes.bool.isRequired,
  isdarkmode: PropTypes.bool.isRequired,
};

export default PhysicalSecurityGuide;
